import clone from 'clone'

export function useArrayListHandler<T>(
  list: MaybeRefOrGetter<MaybeRefOrGetter<T | undefined>[]>,
  itemInitialValue: MaybeRefOrGetter<T>,
  options?: { unshift?: boolean }
) {
  const { cloned: current, sync: clearCurrent } = useCloned<{
    index: number
    item?: T
  }>(
    {
      index: -1,
      item: toValue(itemInitialValue),
    },
    { deep: true }
  )

  const isNew = computed(() => current.value.index < 0)

  function setCurrent(item: T) {
    clearCurrent()
    current.value.item = item
  }

  function setCurrentSafe(item?: T | null) {
    if (isNil(item)) return

    clearCurrent()
    current.value.item = item
  }

  function setCurrentAt(index: number) {
    if (index < 0) {
      clearCurrent()
      return
    }
    current.value = {
      index,
      item: clone(toValue(toValue(list)[index]), false),
    }
  }

  function add(item?: T) {
    toValue(list)[options?.unshift ? 'unshift' : 'push'](item)
  }

  function addCurrent() {
    if (!isNew.value) return

    add(current.value.item)
  }

  function replace(index: number, item?: T) {
    if (index < 0 || isNil(item)) return

    toValue(list).splice(index, 1, item)
  }

  function replaceCurrent() {
    if (isNew.value) return

    replace(current.value.index, current.value.item)
    setCurrentAt(current.value.index)
  }

  function addOrReplaceCurrent() {
    if (isNew.value) {
      addCurrent()
      return
    }
    replaceCurrent()
  }

  function remove(index: number) {
    if (current.value.index === index) clearCurrent()

    toValue(list).splice(index, 1)
  }

  function removeCurrent() {
    if (isNew.value) return

    remove(current.value.index)
  }

  return {
    add,
    addCurrent,
    addOrReplaceCurrent,
    clearCurrent,
    current,
    isNew,
    remove,
    removeCurrent,
    replace,
    replaceCurrent,
    setCurrent,
    setCurrentAt,
    setCurrentSafe,
  }
}
